<template>
  <div>
    <!-- EISENHOWER-PRIORITY -->
    <EventActionFieldRowComponent
      v-if="showEisenhowerPriority === true"
      :label="t('license.quality.components.QAQualityAssuranceComponent.priorityLabel')"
    >
      <InputRow
        v-model="event.eisenhowerPriority"
        small
        hide-label
        :display-name="t('license.quality.components.QAQualityAssuranceComponent.priorityLabel')"
        name="eisenhowerPriority"
      >
        <template #default>
          <EisenhowerSelector v-model="event.eisenhowerPriority" :disabled="disableEvent" />
        </template>
      </InputRow>
    </EventActionFieldRowComponent>
    <!-- FLAGS -->
    <EventActionFieldRowComponent
      :label="t('license.quality.components.QAQualityAssuranceComponent.flagsLabel')"
    >
      <InputRow
        v-model="flags"
        small
        hide-label
        :display-name="t('license.quality.components.QAQualityAssuranceComponent.flagsLabel')"
        name="flags"
      >
        <template #default="{ field: inputField }">
          <SearchSelect
            v-bind="inputField"
            v-model="computedFlags"
            :mode="SelectMode.FLAG"
            class="w-100"
            :required="false"
            :disabled="disableEvent"
            :filters="{
              siteIds: siteId,
            }"
            :placeholder="
              t('license.quality.components.QAQualityAssuranceComponent.flagsPlaceholder')
            "
            :multiple="true"
          />
        </template>
      </InputRow>
    </EventActionFieldRowComponent>

    <!-- CREATOR -->
    <EventActionFieldRowComponent
      :label="t('license.quality.components.QAQualityAssuranceComponent.creatorLabel')"
    >
      <InputRow
        :model-value="event.ownerId"
        small
        hide-label
        :display-name="t('license.quality.components.QAQualityAssuranceComponent.creatorLabel')"
        name="owner"
        required
      >
        <template #default="{ field: inputField }">
          <SearchSelect
            v-bind="inputField"
            v-model="event.ownerId"
            :mode="SelectMode.USER"
            :disabled="disableEvent"
            :filters="
              siteId == null
                ? undefined
                : {
                    hasRightsOnSite: {
                      siteIds: [siteId],
                      rights: SQDC_ASSING_MINIMUM_RIGHTS,
                    },
                  }
            "
            class="w-100"
            :placeholder="
              t('license.quality.components.QAQualityAssuranceComponent.creatorPlaceholder')
            "
          />
        </template>
      </InputRow>
    </EventActionFieldRowComponent>

    <!-- Occurrence Date -->
    <EventActionFieldRowComponent
      :label="t('license.quality.components.QAQualityAssuranceComponent.dateLabel')"
    >
      <InputRow
        :model-value="occurrenceDate"
        small
        hide-label
        :display-name="t('license.quality.components.QAQualityAssuranceComponent.dateLabel')"
        name="occurrenceDate"
        required
      >
        <template #default="{ field: inputField }">
          <DatePickerComponentNew
            v-bind="inputField"
            v-model="occurrenceDate"
            :disabled="disableEvent"
            name="occurrenceDate"
            :clearable="true"
            :type="DatePickerType.MINUTE"
          />
        </template>
      </InputRow>
    </EventActionFieldRowComponent>

    <!-- TECHNICAL PLACE -->
    <EventActionFieldRowComponent
      :label="t('license.quality.components.QAQualityAssuranceComponent.technicalPlaceLabel')"
    >
      <InputRow
        v-model="event.tpId"
        small
        hide-label
        :display-name="
          t('license.quality.components.QAQualityAssuranceComponent.technicalPlaceLabel')
        "
        name="technicalPlaceId"
        required
      >
        <template #default="{ field: inputField }">
          <TechnicalPlaceHierarchySelect
            v-bind="inputField"
            v-model="event.tpId"
            :disabled="disableEvent"
            :variables="tpVariables"
            class="w-100"
          /> </template
      ></InputRow>
    </EventActionFieldRowComponent>
    <div>
      <!-- ORDER NUMBER: -->
      <EventActionFieldRowComponent
        :label="t('license.quality.components.QAQualityAssuranceComponent.productionPlanLabel')"
      >
        <!-- 1) When QA-Setting (BDE) ==> OrderNumber from ProductionPlan -->
        <InputRow
          v-if="enableBDEOrderSelector === true"
          v-model="event.productionPlanId"
          small
          hide-label
          :display-name="
            t('license.quality.components.QAQualityAssuranceComponent.productionPlanLabel')
          "
          name="productionPlanId"
          :required="orderNumberRequired"
        >
          <template #default="{ field: inputField }">
            <PrimeVueSelect
              v-bind="inputField"
              v-model="event.productionPlanId"
              filter
              :show-clear="!orderNumberRequired"
              :options="orderNumberOptions"
              :disabled="disableEvent"
              option-label="label"
              option-value="value"
              class="w-100"
              :placeholder="
                orderNumberOptions?.length > 0
                  ? t(
                      'license.quality.components.QAQualityAssuranceComponent.selectOrderPlaceholder',
                    )
                  : t('license.quality.components.QAQualityAssuranceComponent.noOrderPlaceholder')
              "
            >
              <template #option="slotProps">
                <div class="flex align-items-center">
                  <div>{{ slotProps.option.label }}</div>
                  <tooltip
                    v-if="slotProps.option.value === -1"
                    :text="t('license.quality.components.QAQualityAssuranceComponent.tooltipInfo')"
                    class="ml-1"
                  />
                </div>
              </template>
            </PrimeVueSelect> </template
        ></InputRow>
        <!-- 2) When QA-Setting (Free text) ==> OrderNumber as free text -->
        <InputRow
          v-else
          v-model="event.orderNumber"
          small
          hide-label
          :display-name="
            t('license.quality.components.QAQualityAssuranceComponent.productionPlanLabel')
          "
          name="productionPlanId"
          class="w-100"
          :disabled="disableEvent"
          :placeholder="
            t('license.quality.components.QAQualityAssuranceComponent.inputOrderPlaceholder')
          "
          autofocus
          :required="orderNumberRequired"
          @update:model-value="setProductionPlanNull"
        />
      </EventActionFieldRowComponent>
      <!-- INPUT MATERIAL -->
      <div v-if="hideInputMaterial === false">
        <EventActionFieldRowComponent
          :label="t('license.quality.components.QAQualityAssuranceComponent.inputMaterialLabel')"
        >
          <InputRow
            v-model="inputMaterial"
            small
            hide-label
            :display-name="
              t('license.quality.components.QAQualityAssuranceComponent.inputMaterialLabel')
            "
            name="inputMaterial"
            required
          >
            <template #default="{ field: inputField }">
              <PrimeVueSelect
                v-bind="inputField"
                v-model="inputMaterial"
                :disabled="disableEvent"
                filter
                :options="productFamilyOptions"
                option-label="label"
                option-value="value"
                class="w-100"
              />
            </template>
          </InputRow>
        </EventActionFieldRowComponent>
      </div>

      <!-- OUTPUT MATERIAL -->
      <div v-if="hideOutputMaterial === false">
        <EventActionFieldRowComponent
          :label="t('license.quality.components.QAQualityAssuranceComponent.outputMaterialLabel')"
        >
          <InputRow
            v-model="outputMaterial"
            small
            hide-label
            :display-name="
              t('license.quality.components.QAQualityAssuranceComponent.outputMaterialLabel')
            "
            name="outputMaterial"
            required
          >
            <template #default="{ field: inputField }">
              <PrimeVueSelect
                v-bind="inputField"
                v-model="outputMaterial"
                filter
                :options="productFamilyOptions"
                :disabled="disableEvent"
                option-label="label"
                option-value="value"
                class="w-100"
              /> </template
          ></InputRow>
        </EventActionFieldRowComponent>
      </div>

      <div>
        <!-- Error Occurrence -->
        <EventActionFieldRowComponent
          v-if="hideErrorOccurrenceOn === false"
          :label="t('license.quality.components.QAQualityAssuranceComponent.errorOccurrenceLabel')"
        >
          <div class="flex flex-wrap gap-3 mt-2 mb-1">
            <div class="flex align-items-center">
              <RadioButton
                v-model="event.errorOccurrenceOn"
                input-id="input"
                name="input"
                :disabled="disableEvent"
                :value="ErrorOccurrenceOn.INPUT"
              />
              <label for="input" class="ml-2">{{
                t("license.quality.components.QAQualityAssuranceComponent.inputMaterialLabel")
              }}</label>
            </div>
            <div class="flex align-items-center">
              <RadioButton
                v-model="event.errorOccurrenceOn"
                input-id="output"
                name="output"
                :disabled="disableEvent"
                :value="ErrorOccurrenceOn.OUTPUT"
              />
              <label for="output" class="ml-2">{{
                t("license.quality.components.QAQualityAssuranceComponent.outputMaterialLabel")
              }}</label>
            </div>
          </div>
        </EventActionFieldRowComponent>
        <!-- ERROR CATALOGS -->
        <EventActionFieldRowComponent
          :label="t('license.quality.components.QAQualityAssuranceComponent.errorCatalogsLabel')"
        >
          <InputRow
            :model-value="errorCatalogsIds"
            small
            hide-label
            :display-name="
              t('license.quality.components.QAQualityAssuranceComponent.errorCatalogsLabel')
            "
            name="errorCatalogs"
          >
            <template #default="{ field: inputField }">
              <MultiSelect
                v-bind="inputField"
                v-model="errorCatalogsIds"
                :options="errorCatalogsOptions"
                :disabled="disableEvent"
                display="chip"
                option-label="label"
                option-value="value"
                filter
                class="w-100"
              /> </template
          ></InputRow>
        </EventActionFieldRowComponent>
      </div>
    </div>
    <EventActionFieldRowComponent
      v-if="resultQAConfig"
      :label="`${resultQAConfig.datapointType?.name} ${resultQAConfig.datapointType?.unit ? '[' + resultQAConfig.datapointType.unit + ']' : ''}`"
    >
      <InputGroup>
        <LISAInputNumber
          v-model="qaDatapointValue"
          :min-fraction-digits="2"
          :max-fraction-digits="5"
          :disabled="isDatapointConfigured === false || disableEvent"
          class="w-100"
        ></LISAInputNumber>
        <InputGroupAddon class="p-0">
          <LISAButton
            v-if="isDatapointConfigured"
            icon="fas fa-calculator"
            :style="{ 'border-radius': '0' }"
            :disabled="disableEvent"
            @click="calculatorOP?.show"
          />
          <LISAButton
            v-if="isDatapointConfigured"
            :style="{ 'border-radius': '0 4px 4px 0' }"
            icon="fas fa-times"
            :disabled="disableEvent"
            severity="secondary"
            @click="qaDatapointValue = undefined"
          ></LISAButton>
          <div v-else>
            <tooltip>
              {{ t("license.quality.components.QAQualityAssuranceComponent.datapointNoConfig") }}
            </tooltip>
          </div>
        </InputGroupAddon>
      </InputGroup>
      <Popover ref="calculatorOP">
        <CalculatorComponent
          v-model="qaDatapointValue"
          :disabled="disableEvent"
        ></CalculatorComponent>
      </Popover>
    </EventActionFieldRowComponent>

    <!-- DESCRIPTION -->
    <EventActionFieldRowComponent
      :label="t('license.quality.components.QAQualityAssuranceComponent.descriptionLabel')"
    >
      <InputRow
        v-model="event.description"
        small
        hide-label
        :display-name="t('license.quality.components.QAQualityAssuranceComponent.descriptionLabel')"
        name="description"
        required
      >
        <template #default="{ field: inputField, validation }">
          <TextareaPrimevue
            v-bind="inputField"
            v-model="event.description"
            name="description"
            rows="4"
            class="w-100"
            :disabled="disableEvent"
            :class="validation.valid ? 'p-valid' : 'p-invalid'"
            :placeholder="
              t('license.quality.components.QAQualityAssuranceComponent.descriptionPlaceholder')
            "
            autofocus
            @keydown.meta.enter="$emit('save', false)"
            @keydown.ctrl.enter="$emit('save', false)"
          /> </template
      ></InputRow>
    </EventActionFieldRowComponent>

    <!-- ATTACH -->
    <EventActionFieldRowComponent>
      <FMFileInlineUploadPreview
        v-model:files-ids="event.fileIds"
        formatted-thumbnails
        removable-thumbnails
        :disabled="disableEvent"
        automatic-screenshot
      />
    </EventActionFieldRowComponent>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, type PropType, watch, onMounted } from "vue";
import TextareaPrimevue from "primevue/textarea";
import PrimeVueSelect from "primevue/select";
import MultiSelect from "primevue/multiselect";
import RadioButton from "primevue/radiobutton";
import { useStore } from "vuex";
import { useTranslation } from "@/language/translation";
import tooltip from "@/components/shared/InfoTooltip.vue";
import DatePickerComponentNew from "@/components/shared/DatePickerComponentNew.vue";
import {
  ErrorOccurrenceOn,
  OrderNumberOrigin,
  useQAProductFamiliesQuery,
  useBDEGetAllProductionPlanByTPIdQuery,
  QASettingFailureSourceDefault,
} from "@/graphql";
import { DatePickerType } from "@/components/shared/DatePickerComponentHelper";
import EventActionFieldRowComponent from "@/components/event-action/EventActionFieldRowComponent.vue";
import InputRow from "@/components/shared/form/InputRow.vue";
import type { QualityAssuranceEditType } from "@/components/event-action/event/modal/EventEditType";
import TechnicalPlaceHierarchySelect from "@/modules/organization/components/selects/TechnicalPlaceSelect.vue";
import { isModuleActive, LicenseModule } from "@/functions/moduleConfig";
import LISAButton from "../../../components/shared/primevue/LISAButton.vue";
import CalculatorComponent from "../../../components/shared/CalculatorComponent.vue";
import LISAInputNumber from "@/components/primevueWrapper/LISAInputNumber.vue";
import Popover from "primevue/popover";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import EisenhowerSelector from "@/components/event-action/event/eisenhowerMatrix/EisenhowerSelector.vue";
import { useQAConfig, useSpecifiedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";
import { BoardIdInjectionKey } from "../../../views/event-action/EventActionBoardInjectionKeys";
import SearchSelect from "@/components/shared/searchSelects/SearchSelect.vue";
import { SelectMode } from "@/components/shared/searchSelects/SelectDefinitions";
import { DateTime } from "luxon";
import { DisableEventInjectionKey } from "@/components/event-action/issue/IssueTypes";
import injectAsserted from "@/functions/assert/injectAsserted";
import { SQDC_ASSING_MINIMUM_RIGHTS } from "@/components/event-action/task/helper/SQDCHelper";
import FMFileInlineUploadPreview from "@/modules/filemanagement/components/FMFileInlineUploadPreview.vue";
import { formatCodeName } from "../helper/qaLanguage";
import { useSQDCGlobalSettings } from "@/modules/sqdc/helper/useSQDCGlobalSettings";

const props = defineProps({
  event: {
    type: Object as PropType<QualityAssuranceEditType>,
    required: true,
  },
});
defineEmits(["save"]);

const store = useStore();
const { locale, t } = useTranslation();

const injectedBoardId = inject(BoardIdInjectionKey, ref<number | null>(null));
const boardId = ref<number>();

onMounted(() => {
  boardId.value =
    props.event?.boardConnection?.find((con) => con.status === 0)?.boardId ??
    props.event?.boardConnection?.[0]?.boardId ??
    injectedBoardId?.value;
});
const { siteId } = useSpecifiedBoardContext(boardId);

const { resultQAConfig } = useQAConfig(siteId);

const disableEvent = injectAsserted(DisableEventInjectionKey);

const hideErrorOccurrenceOn = computed(
  () =>
    resultQAConfig?.value?.failureSourceDefault !== QASettingFailureSourceDefault.INPUT_AND_OUTPUT,
);
const hideInputMaterial = computed(
  () => resultQAConfig?.value?.failureSourceDefault === QASettingFailureSourceDefault.OUTPUT,
);
const hideOutputMaterial = computed(
  () => resultQAConfig?.value?.failureSourceDefault === QASettingFailureSourceDefault.INPUT,
);
const orderNumberRequired = computed(() => resultQAConfig?.value?.orderNumberRequired === true);

const calculatorOP = ref<InstanceType<typeof Popover>>();
const qaDatapointValue = computed({
  get() {
    return props.event?.qaDatapoint?.quantity;
  },
  set(val) {
    if (val == null) {
      props.event.qaDatapoint = undefined;
    } else {
      props.event.qaDatapoint = {
        quantity: val,
      };
    }
  },
});

const flags = computed({
  get() {
    if (props.event.flags == null) {
      return [];
    }
    return props.event.flags;
  },
  set(val) {
    props.event.flags = val;
  },
});

const computedFlags = computed<number[]>({
  get() {
    return props.event.flags.map((flag) => flag.id);
  },
  set(value: number[]) {
    props.event.flags = value.map((id) => ({ id }));
  },
});

const occurrenceDate = computed({
  get() {
    if (props.event.occurrenceDate == null) {
      return undefined;
    }
    return DateTime.fromISO(props.event.occurrenceDate);
  },
  set(val: DateTime | undefined) {
    props.event.occurrenceDate = val?.toISO() ?? undefined;
  },
});
const tpVariables = ref<{
  siteIds: number[] | undefined;
  selfAndDescendantsOfTPId: number | undefined;
}>({
  siteIds:
    store.getters["login/preferredSite"] == null
      ? undefined
      : [Number(store.getters["login/preferredSite"])],
  selfAndDescendantsOfTPId: undefined,
});

const isDatapointConfigured = computed(() => resultQAConfig?.value?.datapointTypeId != null);

const orderNumberOrigin = computed(() => resultQAConfig?.value?.orderNumberOriginType);

const enableBDEOrderSelector = computed(
  () => isModuleActive(LicenseModule.BDE) && orderNumberOrigin.value === OrderNumberOrigin.BDE,
);

const { result: getAllProductionPlanByTPIdResult } = useBDEGetAllProductionPlanByTPIdQuery(
  () => ({ technicalPlaceId: props.event.tpId }),
  () => ({
    enabled: props.event.tpId !== undefined && enableBDEOrderSelector.value === true,
  }),
);

const productionPlans = computed(
  () => getAllProductionPlanByTPIdResult.value?.BDEGetAllProductionPlanByTPId ?? [],
);

const orderNumberOptions = computed(() =>
  productionPlans?.value?.map((productionPlan) => ({
    value: productionPlan.id,
    label: productionPlan.orderNumber,
  })),
);

const { result } = useQAProductFamiliesQuery();

const productFamilies = computed(() => result.value?.QAProductFamilies ?? []);

const productFamilyOptions = computed(() => {
  const options =
    result.value?.QAProductFamilies?.flatMap((productFamily) => {
      const productFamilyName = formatCodeName(productFamily as any, locale.value, t);
      if (productFamily?.characteristics?.length === 0) {
        return [
          {
            label: productFamilyName,
            value: [productFamily?.id, undefined],
          },
        ];
      } else {
        return productFamily?.characteristics?.map((characteristic) => ({
          label: `${productFamilyName} [${characteristic?.name || ""}]`,
          value: [productFamily?.id, characteristic?.id],
        }));
      }
    }).sort((a, b) => (a?.label ?? "").localeCompare(b?.label ?? "")) || [];
  return options;
});

const inputMaterial = computed({
  get() {
    return [
      props.event.inputMaterialId ?? undefined,
      props.event.inputMaterialCharacteristicId ?? undefined,
    ];
  },
  set(val) {
    if (val == null) {
      props.event.inputMaterialId = undefined;
      props.event.inputMaterialCharacteristicId = undefined;
    } else {
      props.event.inputMaterialId = val[0];
      props.event.inputMaterialCharacteristicId = val[1];
    }
  },
});

const outputMaterial = computed({
  get() {
    return [
      props.event.outputMaterialId ?? undefined,
      props.event.outputMaterialCharacteristicId ?? undefined,
    ];
  },
  set(val) {
    if (val == null) {
      props.event.outputMaterialId = undefined;
      props.event.outputMaterialCharacteristicId = undefined;
    } else {
      props.event.outputMaterialId = val[0];
      props.event.outputMaterialCharacteristicId = val[1];
    }
  },
});

const errorCatalogsOptions = computed(() => {
  if (props.event.errorOccurrenceOn === ErrorOccurrenceOn.INPUT) {
    return (
      productFamilies.value
        .find((family) => family?.id === props.event.inputMaterialId)
        ?.errorCatalogs?.map((error) => ({
          label: formatCodeName(error as any, locale.value, t),
          value: error?.id,
        }))
        .sort((a, b) => String(a.label ?? "").localeCompare(String(b.label ?? ""))) || []
    );
  }
  return (
    productFamilies.value
      .find((family) => family?.id === props.event.outputMaterialId)
      ?.errorCatalogs?.map((error) => ({
        label: formatCodeName(error as any, locale.value, t),
        value: error?.id,
      }))
      .sort((a, b) => String(a.label ?? "").localeCompare(String(b.label ?? ""))) || []
  );
});

const { showEisenhowerPriority } = useSQDCGlobalSettings();
const errorCatalogsIds = computed({
  get() {
    if (props.event.errorCatalogs) {
      return props.event.errorCatalogs.map((element) => element.id);
    }
    return [];
  },
  set(value: number[]) {
    props.event.errorCatalogs = value.map((element) => ({
      __typename: "QAErrorCatalogue",
      id: element,
      name: errorCatalogsOptions.value.find((option) => option.value === element)?.label as string,
      code: "",
      nameId: 0,
      Name: {},
    })) as any;
  },
});

watch(
  computed(() => props.event.inputMaterialId),
  () => {
    if (props.event.errorOccurrenceOn === ErrorOccurrenceOn.INPUT) {
      errorCatalogsIds.value = errorCatalogsIds.value?.filter((error) =>
        errorCatalogsOptions.value.find((option) => option.value === error),
      );
    }
  },
);
watch(
  computed(() => props.event.outputMaterialId),
  () => {
    if (props.event.errorOccurrenceOn === ErrorOccurrenceOn.OUTPUT) {
      errorCatalogsIds.value = errorCatalogsIds.value?.filter((error) =>
        errorCatalogsOptions.value.find((option) => option.value === error),
      );
    }
  },
);
watch(
  computed(() => props.event.errorOccurrenceOn),
  () => {
    errorCatalogsIds.value = errorCatalogsIds.value?.filter((error) =>
      errorCatalogsOptions.value.find((option) => option.value === error),
    );
  },
);

const setProductionPlanNull = () => {
  props.event.productionPlanId = undefined;
};
</script>
